import { z } from 'zod';
import {
  CustomHeaderValueSchema,
  CustomHeadersInputSchema,
  CustomHeadersSchema,
  HeaderTokenNameSchema,
  ProxyAuthMechanismSchema,
  ResourceOwnerInputSchema,
  withUniqueHeaderNames,
} from '@reflex/shared';

// --- MCP Server Config ---

/**
 * MCP client configuration names become part of generated tool identifiers.
 * Keep them portable across Codex and other clients that only accept letters,
 * numbers, hyphens, and underscores in server names.
 */
export const McpServerNameSchema = z
  .string()
  .min(1, 'Name is required')
  .regex(/^[A-Za-z0-9_-]+$/, 'Use only letters, numbers, hyphens, and underscores.');

/**
 * Persisted shape for one env var on a local MCP server. Plain vars carry
 * their `value` inline. Secret vars carry a `secretId` pointer into the
 * `secrets` table (manager='mcpServers' partition); the cleartext is read
 * from there and lazily promoted into Runloop at devbox launch.
 */
export const McpEnvVarSchema = z.discriminatedUnion('isSecret', [
  z.object({ name: z.string(), isSecret: z.literal(false), value: z.string() }),
  z.object({
    name: z.string(),
    isSecret: z.literal(true),
    secretId: z.string().optional(),
  }),
]);
export type McpEnvVar = z.infer<typeof McpEnvVarSchema>;

/**
 * Input shape for create/update of a local MCP server. Secret vars carry
 * the plaintext `secretValue`; the server stores it under the
 * `mcpServers` partition and replaces it with a `secretId` pointer in
 * the persisted row.
 *
 * On the update path, an empty `secretValue` means "leave the at-rest
 * row alone" — the server preserves the prior `secretId` for that env
 * var name. The create path rejects empties at the route handler since
 * there's nothing to preserve.
 */
export const McpEnvVarInputSchema = z.discriminatedUnion('isSecret', [
  z.object({ name: z.string(), isSecret: z.literal(false), value: z.string() }),
  z.object({ name: z.string(), isSecret: z.literal(true), secretValue: z.string() }),
]);
export type McpEnvVarInput = z.infer<typeof McpEnvVarInputSchema>;

/**
 * How the agent reaches a `mode: 'local'` server. Local servers are wired
 * straight into the agent's native MCP config on the devbox — no Runloop
 * MCP Hub in between — so either transport an MCP client speaks works:
 *
 * - `'stdio'` — the agent spawns `command args...` with `envVars`.
 * - `'http'`  — the agent connects to `endpoint` over streamable HTTP,
 *   sending `headers`. No credential is required; a server that needs one
 *   takes it as a (secret) header.
 */
export const McpLocalTransportSchema = z.enum(['stdio', 'http']);
export type McpLocalTransport = z.infer<typeof McpLocalTransportSchema>;

/**
 * A local `http` server's URL. The agent dials it directly from the devbox,
 * so only `http:` / `https:` make sense; `z.string().url()` alone would also
 * accept `file:` or `javascript:`.
 */
export const McpLocalEndpointSchema = z
  .string()
  .url('Must be a valid URL')
  .refine((url) => /^https?:\/\//i.test(url), 'Use an http:// or https:// URL.');

/**
 * Header list for a local `http` server, on the write path. Same plain/secret
 * shape as {@link McpEnvVarInputSchema} (an empty `secretValue` on update keeps
 * the stored secret), parsed with the shared header grammar: names are header
 * tokens, unique case-insensitively, and values cannot carry line breaks.
 *
 * Unlike a proxied config's custom headers, `Authorization` is allowed: no
 * proxy sets it here, so it is how a bearer-token server gets its credential.
 */
export const McpLocalHeadersInputSchema = withUniqueHeaderNames(
  z.array(
    z.discriminatedUnion('isSecret', [
      z.object({
        name: HeaderTokenNameSchema,
        isSecret: z.literal(false),
        value: CustomHeaderValueSchema,
      }),
      z.object({
        name: HeaderTokenNameSchema,
        isSecret: z.literal(true),
        secretValue: CustomHeaderValueSchema,
      }),
    ]),
  ),
);

/**
 * How agents authenticate to a remote MCP server.
 *
 * - `'bearer'` — a stored token pasted at create time (the legacy default).
 * - `'oauth'`  — per-user tokens minted through the generic MCP OAuth flow.
 *   Each user connects their own account; agents launched by a user who
 *   hasn't connected fail closed with a "connect first" reason. The exact
 *   authorize/token dance is chosen by {@link McpOAuthStrategySchema}.
 * - `'none'`   — public server with no credential. A placeholder secret
 *   keeps the Runloop Hub devbox-param shape.
 */
export const McpAuthTypeSchema = z.enum(['bearer', 'oauth', 'none']);
export type McpAuthType = z.infer<typeof McpAuthTypeSchema>;

/**
 * Which OAuth *strategy* an `authType='oauth'` server uses to mint the
 * per-user token. Every strategy lands the resulting token in
 * plugin-mcp's own connection store (`mcp_oauth_connections`); they
 * differ only in how the client is registered and how the
 * authorize/token exchange runs.
 *
 * - `'dcr'` — the default, and the only one plugin-mcp implements. RFC
 *   9728 discovery resolves the authorization server, RFC 7591 *dynamic
 *   client registration* mints a per-config public client, and the
 *   browser runs a PKCE authorization-code flow. This is what Cursor and
 *   Claude do when you add an arbitrary remote server.
 * - Any other value is the id of a *provider strategy* registered into
 *   `ctx.services.mcpOAuthProviders` by the vendor's own plugin, for a
 *   hosted MCP server whose authorization server publishes no
 *   `registration_endpoint` (`'slack'`, `'github'`). Those flows use an
 *   OAuth app the deployment registers once; the vendor plugin owns the
 *   endpoints, scopes, and credential keys. Hence a free-form string:
 *   which ids exist depends on which plugins the deployment loaded, and
 *   an unknown id resolves to no strategy rather than a parse error.
 *
 * A provider strategy is bound to that provider's own MCP host, so the
 * value is only ever honored for an endpoint on that host — see
 * `resolveMcpOAuthProvider`.
 *
 * Enterprise-Managed Authorization (ID-JAG) is deliberately NOT a value here.
 * It is not a stored property of a config: it is decided per launch, by asking
 * whether the org trusts an issuer and whether the third party advertises
 * `grant-profile:id-jag` (see `McpIdJagService.isAvailableFor`). A stored
 * `'id-jag'` would be a cached answer to a question with two remote inputs,
 * wrong the moment either changed — and it would widen the public API contract
 * with a value nothing ever writes. `McpOAuthStatus.managedByIdp` reports the
 * live answer instead.
 */
export const McpOAuthStrategySchema = z.string().min(1).max(64);
export type McpOAuthStrategy = z.infer<typeof McpOAuthStrategySchema>;

/**
 * Sentinel `oauth_provider` value for configs whose tokens are minted by
 * plugin-mcp's own generic OAuth flow (as opposed to plugin-owned
 * providers like `'slack'` that register into the
 * `OAuthTokenProviderRegistry`).
 */
export const MCP_OAUTH_PROVIDER = 'mcp-oauth';

/**
 * Same-origin `BroadcastChannel` name the pre-create OAuth popup and its
 * opener rendezvous on. Single source of truth so the server (which
 * broadcasts from the callback HTML) and the web opener (which listens)
 * never drift. Messages are additionally scoped to the flow's `state`
 * nonce (see `channelId` on {@link McpOAuthPopupMessage}) so a concurrent
 * flow in another same-origin tab can't settle on the wrong result.
 */
export const MCP_OAUTH_BROADCAST_CHANNEL = 'mcp-oauth';

/**
 * Result the pre-create OAuth popup hands back to its opener, over both
 * `window.opener.postMessage` and the same-origin
 * {@link MCP_OAUTH_BROADCAST_CHANNEL}. `channelId` is the flow's `state`
 * nonce, which the opener knows (it appears in the authorize URL it
 * navigates the popup to) and the callback knows (it arrives as the
 * `state` query param); the opener ignores channel messages whose
 * `channelId` isn't its own.
 */
export interface McpOAuthPopupMessage {
  source: typeof MCP_OAUTH_BROADCAST_CHANNEL;
  mcp_oauth: 'connected' | 'error';
  /** Flow `state` nonce this result belongs to. */
  channelId?: string;
  /** Set on success: id of the newly created, connected config. */
  config?: string;
  /** Set on failure: a short reason code. */
  reason?: string;
}

/**
 * Cached OAuth client registration persisted on the config row
 * (`oauth_client_info` jsonb). One registration is shared by every user
 * who connects; it is re-minted when the discovered authorization
 * server no longer matches `authorizationServerUrl`.
 */
export const McpOAuthClientInfoSchema = z.object({
  /**
   * Authorization server the registration was minted by. For the `'dcr'`
   * strategy this is the RFC 8414-discovered server; a provider strategy
   * stores its own `authorizationServerUrl` (the vendor's issuer, or a
   * fixed marker for a vendor that publishes no discovery document) and
   * never re-discovers against it.
   */
  authorizationServerUrl: z.string(),
  /**
   * The OAuth client id. For `'dcr'` this is the dynamically-registered
   * client; for a provider strategy it is the deployment's own app id.
   */
  clientId: z.string(),
  /**
   * Pointer into the encrypted `mcpServers` secrets partition for the
   * client secret, when the authorization server issued one despite the
   * public-client (`token_endpoint_auth_method: 'none'`) registration.
   * Never the plaintext — credentials always live in the encrypted
   * secrets store. Unused by provider strategies, whose app secrets are
   * re-resolved by the owning plugin at exchange time.
   */
  clientSecretId: z.string().optional(),
  redirectUri: z.string(),
  /**
   * OAuth strategy this registration was minted for. Optional (not
   * defaulted) so rows serialized before the strategy split stay
   * assignable; readers treat `undefined` as `'dcr'`.
   */
  strategy: McpOAuthStrategySchema.optional(),
});
export type McpOAuthClientInfo = z.infer<typeof McpOAuthClientInfoSchema>;

export const McpServerConfigSchema = z.object({
  id: z.string(),
  name: z.string(),
  /**
   * `'remote'` — HTTP-based server routed through Runloop's MCP Hub (default).
   * `'local'`  — wired straight into the agent's config on the devbox,
   *   either as a subprocess or a direct HTTP connection (see `transport`).
   */
  mode: z.enum(['remote', 'local']).default('remote'),
  /**
   * See {@link McpLocalTransportSchema}. `null` for remote rows. Optional
   * (not defaulted) for the same reason as `authType`; readers treat an
   * absent value on a local row as `'stdio'`.
   */
  transport: McpLocalTransportSchema.nullable().optional(),
  endpoint: z.string().url().nullable(),
  allowedTools: z.array(z.string()).nullable(),
  description: z.string().nullable(),
  secretName: z.string().nullable(),
  runloopConfigId: z.string().nullable(),
  enabled: z.boolean(),
  createdAt: z.number(),
  /**
   * When non-null, the Runloop secret used for this server is resolved
   * per-agent from `ctx.services.oauthTokenProviders.get(oauthProvider)`.
   * Plugins that own an OAuth connection (e.g. plugin-slack) register a
   * provider and set this on their managed config rows; legacy admin-
   * pasted configs leave this null and use the shared `secretName`.
   */
  oauthProvider: z.string().nullable(),
  /**
   * See {@link McpAuthTypeSchema}. Always `'bearer'` for local rows.
   * Optional (not defaulted) so pre-existing fixtures and older
   * serialized payloads stay assignable; the server always populates
   * it, and readers treat `undefined` as `'bearer'`.
   */
  authType: McpAuthTypeSchema.optional(),
  /**
   * How Runloop's MCP proxy presents the resolved credential to the *upstream*
   * server. A different axis from {@link authType}, which says where reflex
   * gets that credential — every combination is meaningful, e.g. an
   * `authType: 'oauth'` token that the upstream expects in `x-api-key` rather
   * than as a bearer. Never fold the two together.
   *
   * Meaningless for `mode: 'local'` rows, which are a subprocess rather than a
   * proxied request.
   *
   * Optional (not defaulted), matching {@link authType} above: the server
   * always populates it — rows predating the column normalize to
   * `{ type: 'bearer' }`, which is what they were created as — but a payload
   * serialized by an older build, or cached in a browser tab across a rolling
   * deploy, legitimately arrives without it. Readers treat `undefined` as
   * `DEFAULT_PROXY_AUTH_MECHANISM`.
   */
  runloopAuthMechanism: ProxyAuthMechanismSchema.optional(),
  /**
   * Extra headers Runloop adds to upstream requests after the credential.
   * Secret-backed entries carry only their Runloop secret name — the value is
   * written to Runloop once and is never stored at rest in reflex or returned
   * here.
   *
   * Optional for the same reason as {@link runloopAuthMechanism}; readers
   * treat `undefined` as an empty list.
   */
  customHeaders: CustomHeadersSchema.optional(),
  /**
   * Organization that owns this MCP server config. Always set — the
   * column is `NOT NULL` and there is no host-shared tier, so the server
   * scopes every read and mutation to rows matching the caller's active
   * organization exactly.
   */
  organizationId: z.string(),
  /**
   * Per-resource owner. A prefixed id whose kind is derived from the
   * id's prefix. The server's owner-scope helpers admit a row when
   * `ownerId` is null (host-tier) or matches one of the caller's owner
   * ids (user, organization, teams, plugin-contributed kinds).
   *
   * For legacy rows that pre-date this refactor, the value is the row's
   * organization id (the explicit "org-owned" sentinel populated by the
   * backfill migration).
   */
  ownerId: z.string().nullable(),
  // --- Local mode fields ---
  /** Executable to run (e.g. `"uvx"`). `null` for remote servers. */
  command: z.string().nullable(),
  /** Arguments to pass to the command (e.g. `["mcp-grafana"]`). `null` for remote servers. */
  args: z.array(z.string()).nullable(),
  /** Environment variables for the local server process. `null` for remote servers. */
  envVars: z.array(McpEnvVarSchema).nullable(),
  /**
   * Headers the agent sends to a local `transport: 'http'` server. Same
   * plain/secret shape as {@link envVars}: secret values live in the
   * `mcpServers` secrets partition and reach the devbox as Runloop secrets.
   * `null` for every other row. Optional for the same reason as `authType`.
   */
  headers: z.array(McpEnvVarSchema).nullable().optional(),
});
export type McpServerConfig = z.infer<typeof McpServerConfigSchema>;

/**
 * Shape of the `mcp-servers` plugin attachment's stored config. The host hands
 * pickers and summaries an opaque `unknown`, so both ends of this plugin's
 * attachment read the same declaration rather than re-typing it.
 */
export const McpAttachmentValueSchema = z.object({
  mcpServerIds: z.array(z.string()),
});
export type McpAttachmentValue = z.infer<typeof McpAttachmentValueSchema>;

/** Stable empty result so callers can memoize on the returned reference. */
const NO_MCP_SERVER_IDS: string[] = [];

/**
 * Read the attached server ids out of an attachment config. Returns the stored
 * array itself, not a copy, so React memos keyed on it stay stable across
 * renders that did not change the selection.
 */
export function mcpServerIdsFromAttachmentValue(value: unknown): string[] {
  return (value as McpAttachmentValue | null)?.mcpServerIds ?? NO_MCP_SERVER_IDS;
}

export const CreateMcpServerConfigSchema = z.object({
  name: McpServerNameSchema,
  endpoint: z.string().url(),
  allowedTools: z.array(z.string()).min(1),
  description: z.string().optional(),
  /**
   * Auth method for the new server. Defaults to `'bearer'` (which
   * requires `secretValue`). `'oauth'` rows carry no shared secret —
   * each user connects via `POST /mcp-configs/:id/oauth/start` after
   * creation. `'none'` stores a placeholder credential.
   *
   * Kept as a plain field (no cross-field `refine`) so the schema stays
   * a `ZodObject` for the auto-generated MCP resource tools; the
   * bearer-requires-secretValue rule is enforced in
   * `McpConfigService.create`.
   */
  authType: McpAuthTypeSchema.optional(),
  /** Bearer token. Required when `authType` is `'bearer'`, ignored otherwise. */
  secretValue: z.string().min(1).optional(),
  /**
   * Where Runloop's proxy puts the resolved credential on upstream requests.
   * Defaults to `{ type: 'bearer' }`. Independent of {@link authType} — see
   * {@link McpServerConfigSchema.runloopAuthMechanism}.
   */
  runloopAuthMechanism: ProxyAuthMechanismSchema.optional(),
  /**
   * Extra headers applied after the credential, at most
   * `MAX_CUSTOM_HEADERS`. Every secret entry must carry its `secretValue` on
   * create — there is nothing stored yet to keep.
   */
  customHeaders: CustomHeadersInputSchema.optional(),
  /**
   * Resource ownership for the new config: a prefixed owner id
   * (`usr_` / `tem_` / `org_` / `prj_`). Required at runtime — the
   * resource-kind registry has no default scope, so an omitted or
   * host-tier (`null`) owner is a 400 naming the eligible scopes. Routes
   * validate membership via `assertCanWriteOwned`.
   */
  owner: ResourceOwnerInputSchema.optional().describe(
    'Owner of the new resource: a usr_/tem_/org_/prj_ id. Required — there is no default scope.',
  ),
});
export type CreateMcpServerConfig = z.infer<typeof CreateMcpServerConfigSchema>;

/**
 * Create input for a local server. Kept a flat `ZodObject` (no discriminated
 * union or `refine`) so it stays usable by the auto-generated MCP resource
 * tools; `McpConfigService.createLocal` enforces that `transport: 'stdio'`
 * carries a `command` and `transport: 'http'` an `endpoint`.
 */
export const CreateLocalMcpServerConfigSchema = z.object({
  name: McpServerNameSchema,
  /** Defaults to `'stdio'`, the only transport before HTTP was added. */
  transport: McpLocalTransportSchema.default('stdio'),
  /** Required for `stdio`, rejected for `http`. */
  command: z.string().min(1).optional(),
  args: z.array(z.string()).default([]),
  /** Required for `http`, rejected for `stdio`. */
  endpoint: McpLocalEndpointSchema.optional(),
  /** Headers for an `http` server; see {@link McpLocalHeadersInputSchema}. */
  headers: McpLocalHeadersInputSchema.default([]),
  /**
   * Input-side env vars: secret variants carry the plaintext
   * `secretValue`. The service writes the value into the `mcpServers`
   * partition of the secrets table, then persists a `secretId`
   * pointer instead of the plaintext.
   */
  envVars: z.array(McpEnvVarInputSchema).default([]),
  description: z.string().optional(),
  /** See {@link CreateMcpServerConfigSchema.owner}. */
  owner: ResourceOwnerInputSchema.optional().describe(
    'Owner of the new resource: a usr_/tem_/org_/prj_ id. Required — there is no default scope.',
  ),
});
export type CreateLocalMcpServerConfig = z.infer<typeof CreateLocalMcpServerConfigSchema>;
/** Pre-default shape: `transport`, `args`, `envVars`, and `headers` may be omitted. */
export type CreateLocalMcpServerConfigInput = z.input<typeof CreateLocalMcpServerConfigSchema>;

export const UpdateMcpServerConfigSchema = z.object({
  name: McpServerNameSchema.optional(),
  endpoint: z.string().url().optional(),
  allowedTools: z.array(z.string()).min(1).optional(),
  description: z.string().nullable().optional(),
  secretValue: z.string().min(1).optional(),
  /** Set to move the credential to a different position; omit to keep it. */
  runloopAuthMechanism: ProxyAuthMechanismSchema.optional(),
  /**
   * Replaces the custom-header list wholesale (Runloop has no merge
   * semantics), so send the full desired set. Omit to keep the stored list —
   * an omitted field must not clear headers a partial `update_mcp_config` call
   * never mentioned. Pass `[]` to clear.
   *
   * A secret entry may omit `secretValue` to keep the value already stored
   * under that header name; a name with nothing stored is a 400 rather than a
   * dangling reference.
   */
  customHeaders: CustomHeadersInputSchema.optional(),
  /**
   * Resource ownership transfer. When provided, the caller must already
   * be allowed to claim the requested owner (a user can only assign to
   * themselves; a team requires the caller to be a member). Routes
   * enforce this via {@link assertCallerCanOwn}.
   */
  owner: ResourceOwnerInputSchema.optional(),
});
export type UpdateMcpServerConfig = z.infer<typeof UpdateMcpServerConfigSchema>;

/**
 * Update input for a local server. The transport is fixed at create time, so
 * stdio fields are rejected on an `http` row and vice versa.
 */
export const UpdateLocalMcpServerConfigSchema = z.object({
  name: McpServerNameSchema.optional(),
  command: z.string().min(1).optional(),
  args: z.array(z.string()).optional(),
  envVars: z.array(McpEnvVarInputSchema).optional(),
  endpoint: McpLocalEndpointSchema.optional(),
  /** Replaces the header list; a secret entry with an empty value keeps its stored secret. */
  headers: McpLocalHeadersInputSchema.optional(),
  description: z.string().nullable().optional(),
  /** See {@link UpdateMcpServerConfigSchema.owner}. */
  owner: ResourceOwnerInputSchema.optional(),
});
export type UpdateLocalMcpServerConfig = z.infer<typeof UpdateLocalMcpServerConfigSchema>;

// --- API types ---

export const ListMcpServerConfigsResponseSchema = z.array(McpServerConfigSchema);
export type ListMcpServerConfigsResponse = z.infer<typeof ListMcpServerConfigsResponseSchema>;

/**
 * One tool advertised by an upstream MCP server, as returned by
 * `GET /mcp-configs/:id/available-tools`. The list is live — fetched on demand
 * from the upstream via the streamable HTTP transport — not cached in our DB.
 */
export const McpAvailableToolSchema = z.object({
  name: z.string(),
  description: z.string().nullable(),
});
export type McpAvailableTool = z.infer<typeof McpAvailableToolSchema>;

export const ListMcpAvailableToolsResponseSchema = z.object({
  tools: z.array(McpAvailableToolSchema),
});
export type ListMcpAvailableToolsResponse = z.infer<typeof ListMcpAvailableToolsResponseSchema>;

// --- Generic MCP OAuth API types ---

/**
 * Response of `POST /mcp-configs/:id/oauth/start`. The web client
 * navigates the browser to `authorizationUrl`; the provider redirects
 * back to the server-side callback, which finishes the code exchange
 * and lands the user on the MCP servers page.
 */
export const StartMcpOAuthResponseSchema = z.object({
  authorizationUrl: z.string().url(),
});
export type StartMcpOAuthResponse = z.infer<typeof StartMcpOAuthResponseSchema>;

/**
 * Config parameters stashed on an in-flight pre-create OAuth flow row
 * (`mcp_oauth_flows.pending_config`). Authorization always runs as
 * `authType='oauth'`, so no bearer `secretValue` is ever persisted here.
 * The callback turns this into the real config only after the token
 * exchange succeeds — a cancelled or failed connect leaves nothing
 * behind.
 *
 * Deliberately narrower than {@link CreateMcpServerConfigSchema}: no
 * `runloopAuthMechanism` and no `customHeaders`. This payload sits at rest
 * in Postgres for the flow's lifetime, so a secret header's `secretValue`
 * here would be a plaintext header credential stored in reflex — the one
 * thing the custom-header design guarantees never happens. Proxy auth for
 * an OAuth server is set from the edit dialog after the connect; do not
 * "complete" this schema with those fields.
 */
export const PendingMcpOAuthConfigSchema = z.object({
  name: McpServerNameSchema,
  endpoint: z.string().url(),
  allowedTools: z.array(z.string()).min(1),
  description: z.string().optional(),
  owner: ResourceOwnerInputSchema.optional(),
});
export type PendingMcpOAuthConfig = z.infer<typeof PendingMcpOAuthConfigSchema>;

/**
 * Body of `POST /mcp-configs/oauth/preflight`. Runs discovery, dynamic
 * client registration, and mints the authorize URL BEFORE any config
 * row exists; the config is created by the callback once OAuth
 * completes. `returnTo` is a sanitized same-origin app path.
 */
export const PreflightMcpOAuthRequestSchema = PendingMcpOAuthConfigSchema.extend({
  returnTo: z.string().optional(),
  /**
   * Explicit OAuth strategy override. When omitted the server
   * auto-detects it from the endpoint (a registered provider strategy
   * claiming that host, else `'dcr'`), which is what the web "Connect"
   * button relies on.
   */
  strategy: McpOAuthStrategySchema.optional(),
});
export type PreflightMcpOAuthRequest = z.infer<typeof PreflightMcpOAuthRequestSchema>;

/**
 * One provider OAuth strategy this deployment has loaded, as reported by
 * `GET /mcp-configs/oauth/providers`.
 *
 * The add-server form reads these so it can say what a vendor needs
 * *before* the user clicks Connect and leaves Reflex. The strategies are
 * contributed by the vendors' own plugins, so which ones exist is a
 * property of the running deployment, not of the static catalog.
 */
export const McpOAuthProviderInfoSchema = z.object({
  /** Strategy id, e.g. `'github'`. */
  id: z.string(),
  /** Vendor name for prose, e.g. `'GitHub'`. */
  displayName: z.string(),
  /** The MCP endpoint host this strategy claims. */
  mcpHost: z.string(),
  /**
   * One-line prerequisite on the vendor side (register this callback URL
   * on the deployment's OAuth app), shown with the callback URL.
   */
  setupNote: z.string().optional(),
  /** Whether the deployment has the app credentials this flow needs. */
  configured: z.boolean(),
  /**
   * What is missing when {@link configured} is false — the same sentence
   * the connect attempt would fail with, shown up front instead.
   */
  configurationError: z.string().optional(),
});
export type McpOAuthProviderInfo = z.infer<typeof McpOAuthProviderInfoSchema>;

/** Response of `GET /mcp-configs/oauth/providers`. */
export const McpOAuthProvidersResponseSchema = z.object({
  providers: z.array(McpOAuthProviderInfoSchema),
});
export type McpOAuthProvidersResponse = z.infer<typeof McpOAuthProvidersResponseSchema>;

/**
 * Response of `GET /mcp-configs/:id/oauth/status` — the calling user's
 * own connection state for one OAuth-backed server.
 */
export const McpOAuthStatusSchema = z.object({
  /**
   * A usable credential demonstrably exists for this user and server: the
   * browser flow completed and the token is at rest. Deliberately never true
   * for {@link managedByIdp} servers — nothing is stored for those, and the
   * only thing that can answer "would a launch work?" is the exchange itself,
   * which happens at launch. Saying `true` there would be a guess wearing the
   * clothes of a fact.
   */
  connected: z.boolean(),
  /** Access-token expiry (ms epoch); null when unknown or not connected. */
  expiresAt: z.number().nullable(),
  scope: z.string().nullable(),
  /**
   * True when this server is reached through the organization's identity
   * provider (ID-JAG) rather than a per-user consent screen. The UI shows a
   * managed state instead of a Connect button: there is nothing for the user
   * to click, and offering a button that cannot do anything is worse than
   * saying so.
   *
   * What this does *not* claim is that the identity provider will authorize
   * the call. The preconditions the server can check are local — the
   * deployment is a registered client, the org trusts an issuer, the target
   * advertises the grant profile, this user has a stored credential. The
   * admin's app-to-app grant at the IdP, which is the whole point of ID-JAG,
   * is consulted only during the token exchange at agent launch. A managed
   * row therefore reports *managed*, not *verified*.
   *
   * The honest way to strengthen this is to record the outcome of the last
   * exchange per (config, user) and surface that — a real answer rather than
   * a probe minted only to be discarded. Not built yet; until it is, the UI
   * must not render managed as a green go-ahead.
   */
  managedByIdp: z.boolean().default(false),
  /**
   * Set only when `managedByIdp` is true and a precondition we *can* check is
   * missing — for example the user has not completed an SSO sign-in on this
   * deployment, so there is no identity credential to exchange. Explains a
   * state the user cannot fix by clicking Connect.
   *
   * `null` means "nothing known to be wrong", which is weaker than "will
   * work"; see {@link managedByIdp}.
   */
  managedUnavailableReason: z.string().nullable().default(null),
});
export type McpOAuthStatus = z.infer<typeof McpOAuthStatusSchema>;

/**
 * One entity that depends on an MCP server config — a live agent, a persona, or
 * a project that has it attached. `kind` is a stable discriminator the UI groups
 * by; `label` is the entity's display name. Collected across the host and other
 * plugins by the reference-provider registry.
 */
export const McpConfigDependentSchema = z.object({
  kind: z.enum(['agent', 'agent-persona', 'project']),
  id: z.string(),
  label: z.string(),
});
export type McpConfigDependent = z.infer<typeof McpConfigDependentSchema>;

/**
 * Response of `GET /mcp-configs/:id/dependents` — what currently relies on a
 * server, surfaced in the delete-confirm dialog so a permanent delete is an
 * informed one.
 */
export const McpConfigDependentsResponseSchema = z.object({
  dependents: z.array(McpConfigDependentSchema),
});
export type McpConfigDependentsResponse = z.infer<typeof McpConfigDependentsResponseSchema>;

/**
 * Why an attached server id is not in the caller's own server list.
 *
 * The picker reconciles the ids stored on an attachment against
 * `GET /mcp-configs`, which is owner-fenced and enabled-only, so four very
 * different situations collapse into "this id is not in the list". Naming
 * them is the whole point of `GET /mcp-configs/attached`:
 *
 * - `available` — visible and enabled. The reconciliation was stale (the
 *   list refetched since); nothing is wrong.
 * - `disabled` — the row exists and the caller can see it, but it is
 *   toggled off, so it will not be attached at launch.
 * - `no-access` — the row exists in the caller's org, but its owner is not
 *   one the caller belongs to and it was not shared with them.
 * - `not-found` — no such row in the caller's active organization: deleted,
 *   or the attachment was carried over from another org.
 */
export const McpAttachedStatusSchema = z.enum(['available', 'disabled', 'no-access', 'not-found']);
export type McpAttachedStatus = z.infer<typeof McpAttachedStatusSchema>;

/**
 * What one attached id resolves to.
 *
 * `name` is `null` for `not-found` (there is no row to name) and set
 * otherwise — including for `no-access`, which is the deliberate
 * disclosure this endpoint makes: a caller who already holds an
 * unguessable id, attached to something they can see, learns the server's
 * display name and why it is unusable, and nothing else. Endpoint,
 * credentials, tools, and the owner's identity stay behind the fence.
 */
export const McpAttachedConfigSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  status: McpAttachedStatusSchema,
});
export type McpAttachedConfig = z.infer<typeof McpAttachedConfigSchema>;

/** Most ids one attachment may ask about in a single request. */
export const MCP_ATTACHED_IDS_LIMIT = 100;

/**
 * Querystring of `GET /mcp-configs/attached`: a comma-separated `ids` list.
 * Bounded so the endpoint can never be turned into a bulk enumeration of a
 * tenant's server names, and because no real attachment carries anywhere
 * near this many servers.
 */
export const McpAttachedConfigsQuerySchema = z.object({
  ids: z
    .string()
    .transform((raw) =>
      raw
        .split(',')
        .map((id) => id.trim())
        .filter((id) => id.length > 0),
    )
    .pipe(z.array(z.string()).min(1).max(MCP_ATTACHED_IDS_LIMIT)),
});
export type McpAttachedConfigsQuery = z.infer<typeof McpAttachedConfigsQuerySchema>;

/**
 * Response of `GET /mcp-configs/attached?ids=…` — one entry per requested
 * id, in the order they were requested, so the picker can render a name and
 * a reason where it used to render a bare id.
 */
export const McpAttachedConfigsResponseSchema = z.object({
  configs: z.array(McpAttachedConfigSchema),
});
export type McpAttachedConfigsResponse = z.infer<typeof McpAttachedConfigsResponseSchema>;
